import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UISimpleTable from '@/components/shared/UISimpleTable';
import UIButton from '@/components/shared/UIButton';
import UIMap from '@/components/shared/UIMap';
import useProperties from '@/hooks/useProperties';
import './PropertiesDashboardSection.scss';
import { safeToLowerCase } from '@/utils/stringUtils';

const baseClass = 'DashboardPage__PropertiesDashboardSection';

interface SortState {
  column: string | null;
  direction: 'asc' | 'desc' | null;
}

interface PropertiesDashboardSectionProps {
  sortableColumns?: string[]; // Колонки, які можна сортувати
}

function PropertiesDashboardSection({
  sortableColumns = ['name', 'address', 'units'],
}: PropertiesDashboardSectionProps) {
  const navigate = useNavigate();
  const { ownedProperties, totalItems } =
    useProperties();
  const [sortState, setSortState] = useState<SortState>({
    column: null,
    direction: null,
  });

  // Добавляем лог для ownedProperties
  console.log('PropertiesDashboardSection: ownedProperties', ownedProperties);

  // Обмежуємо до 10 записів
  const limitedProperties = useMemo(() => {
    return ownedProperties.slice(0, 10);
  }, [ownedProperties]);

  // Сортовані дані
  const sortedProperties = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return limitedProperties;
    }

    const sorted = [...limitedProperties].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortState.column) {
        case 'name':
          aValue = safeToLowerCase(a.name) || '';
          bValue = safeToLowerCase(b.name) || '';
          break;
        case 'address':
          aValue =
            safeToLowerCase(`${a.address?.street || ''} ${a.address?.city || ''}`);
          bValue =
            safeToLowerCase(`${b.address?.street || ''} ${b.address?.city || ''}`);
          break;
        case 'units':
          aValue = Array.isArray(a.units) ? a.units.length : a.units || 0;
          bValue = Array.isArray(b.units) ? b.units.length : b.units || 0;
          break;
        default:
          return 0;
      }

      if (sortState.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return sorted;
  }, [limitedProperties, sortState]);

  const geoData = useMemo(() => {
    return sortedProperties
      .filter((prop) => {
        return prop.address?.latitude && prop.address?.longitude;
      })
      .map((property) => {
        return {
          label: property.name,
          x: property.address?.longitude || 0,
          y: property.address?.latitude || 0,
          raw: {
            addresstype: 'building' as const,
            type: 'house' as const,
          },
        };
      });
  }, [sortedProperties]);

  const getBadgeInfo = (property: any) => {
    // Добавляем логи для отладки
    console.log('getBadgeInfo: property', property);
    const type =
      safeToLowerCase(property.type);
    console.log('getBadgeInfo: processed type', type);

    switch (type) {
      case 'residential':
        return { text: 'Residential', color: '#76C102' };
      case 'commercial':
        return { text: 'Commercial', color: '#FF9C07' };
      default:
        return { text: 'Residential', color: '#76C102' };
    }
  };

  const handleSort = (columnKey: string, direction: 'asc' | 'desc') => {
    if (sortableColumns.includes(columnKey)) {
      setSortState({ column: columnKey, direction });
    }
  };

  const tableColumns = [
    {
      key: 'name',
      label: 'Property',
      width: '140px',
      sortable: sortableColumns.includes('name'),
      sortDirection: sortState.column === 'name' ? sortState.direction : null,
      showDivider: true,
      render: (value: string, row: any) => {
        const badgeInfo = getBadgeInfo(row);
        return (
          <div className={`${baseClass}__table-property`}>
            <div
              className={`${baseClass}__table-property-name`}
              onClick={() => navigate(`/dashboard/properties/${row.id}`)}
            >
              {row.name}
            </div>
            <div
              className={`${baseClass}__table-property-badge`}
              style={{ backgroundColor: badgeInfo.color }}
            >
              {badgeInfo.text}
            </div>
          </div>
        );
      },
    },
    {
      key: 'address',
      label: 'Address',
      width: '231px',
      sortable: sortableColumns.includes('address'),
      sortDirection:
        sortState.column === 'address' ? sortState.direction : null,
      showDivider: true,
      render: (value: any, row: any) => (
        <div className={`${baseClass}__table-address`}>
          {row.address?.street}, {row.address?.city}
        </div>
      ),
    },
    {
      key: 'units',
      label: 'Units',
      width: '89px',
      sortable: sortableColumns.includes('units'),
      sortDirection: sortState.column === 'units' ? sortState.direction : null,
      showDivider: false,
      render: (value: any, row: any) => (
        <div className={`${baseClass}__table-units`}>
          {Array.isArray(row.units) ? row.units.length : row.units || 0}
        </div>
      ),
    },
  ];

  return (
    <div className={baseClass}>
      {/* Верхня секція з хедером та картою */}
      <div className={`${baseClass}__top-section`}>
        {/* Заголовок */}
        <div className={`${baseClass}__header`}>
          <div className={`${baseClass}__title-line`}>
            <div className={`${baseClass}__title-text`}>
              <span>Properties:</span>
              <span className={`${baseClass}__title-count`}>
                {limitedProperties.length}
                {totalItems > limitedProperties.length
                  ? ` of ${totalItems}`
                  : ''}{' '}
                buildings
              </span>
            </div>
            <div className={`${baseClass}__menu-icon`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="1" fill="#64748B" />
                <circle cx="12" cy="5" r="1" fill="#64748B" />
                <circle cx="12" cy="19" r="1" fill="#64748B" />
              </svg>
            </div>
          </div>
          <div className={`${baseClass}__divider`}></div>
        </div>

        {/* Карта */}
        <div className={`${baseClass}__map-section`}>
          <UIMap geoData={geoData} className={`${baseClass}__map`} />
        </div>
      </div>

      {/* Список властивостей */}
      <div className={`${baseClass}__list-section`}>
        <div className={`${baseClass}__list-header`}>
          <div className={`${baseClass}__list-title`}>List of properties</div>
          <UIButton
            variant="primary"
            size="small"
            onClick={() => navigate('/properties')}
          >
            View All
          </UIButton>
        </div>
        <div className={`${baseClass}__list-divider`}></div>

        <UISimpleTable
          data={sortedProperties}
          columns={tableColumns}
          className={`${baseClass}__table`}
          onSort={handleSort}
        />
      </div>
    </div>
  );
}

export default PropertiesDashboardSection;
